/**
 * **Which civil day one calendar occurrence is drawn on** — R17's rule, and the occurrence's own.
 *
 * Promoted out of `pages/client-calendar/model/clientEvent` on 19 September 2026 on its second
 * consumer, exactly as `occurrenceStatus` was the day before. `/zadania` draws a client's week
 * beside a missed workout, and it has to place a session on the same square the calendar places it
 * on. The first version of that strip compared `displayDate` against a `YYYY-MM-DD` string — and
 * `DisplayDate` is a `DateTime` on the wire, so the comparison could never match: against the real
 * backend every event fell out of the week and the card degraded to "this client has no dated
 * sessions", or drew the attendance run as zeros. The bug was not a wrong rule, it was NOT ASKING
 * the rule that already existed.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 *
 * ── The rule, unchanged from the calendar ────────────────────────────────────────────────────
 * R17 decides which day, and it is `displayDate`: a finished session draws on the day it was
 * finished, an in-progress one on the day it started, everything else on the day it is booked for.
 * The occurrence's OWN day answers only where R17 fell through to the booked slot, and that is a
 * byte equality between two legacy values — `displayDate === eventDate` — because R17 keeps
 * publishing `displayDate` in the legacy bytes for an undiverged row.
 *
 * The occurrence's own day is the local day of its instant where it has one, and the civil date it
 * names where it does not — `moment.date`, read VERBATIM, never through `new Date`, which parses it
 * as UTC midnight and renders it a day early for every reader west of Greenwich.
 *
 * `displayDate` gates placement even when the moment could answer: it is required on the wire, so
 * an unreadable one is a response this build cannot trust rather than a row to rescue — it is placed
 * NOWHERE, by every reader (the client calendar, `/zadania`, the booking grid). A response carrying
 * no `moment` at all — a rolled-back backend — keeps the legacy answer whole.
 *
 * ── Whose clock (24 September 2026) ──────────────────────────────────────────────────────────
 * Every day here is read on ONE clock the caller names: `zone`, an IANA id, or `null` for the
 * reader's own (the browser's local clock, which is what the client calendar and `/zadania` have
 * always meant). The booking grid passes the TRAINER's zone. Where R17 diverged, `displayDate` is
 * a recorded finish or start — a real instant — and its day is that instant read on the same clock.
 * It used to be the UTC prefix of the stamp, which put a session finished at 00:20 in Warsaw on the
 * previous day. A `displayDate` with no clock part (a bare date) is still read verbatim.
 */

import { occurrenceInstant, parseInstant, toIsoDate, type OccurrenceTime } from '~/shared/lib/calendarDate';
import { dateTimeFormat } from '~/shared/lib/intlCache';

/** A calendar day, `YYYY-MM-DD`. */
export type IsoDay = string;

/**
 * The wire fields placement reads, and nothing else. It EXTENDS `OccurrenceTime` rather than
 * restating `moment` and `eventInstant`, so the instant is read by exactly one rule.
 */
export interface OccurrencePlacement extends OccurrenceTime {
  /** R17's placement stamp. A `DateTime` on the wire, so an INSTANT, not a day. */
  readonly displayDate?: string | null;
  /** The legacy scheduled-due stamp. Read for ONE thing: byte equality against `displayDate`. */
  readonly eventDate?: string | null;
}

/** Where an occurrence is drawn: its day, and the instant it stands at (`null` = no hour). */
export interface PlacedOccurrence {
  readonly day: IsoDay;
  readonly atMs: number | null;
}

/** The leading `YYYY-MM-DD` of a wire stamp, or `null` where there is not one. */
export function calendarDayOf(iso: string | null | undefined): IsoDay | null {
  if (!iso) return null;
  return /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : null;
}

/** The day `ms` falls on — on `zone`'s clock, or the reader's own for `null`. */
function dayOn(ms: number, zone: string | null): IsoDay {
  if (zone === null) return toIsoDate(new Date(ms));
  const parts = dateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ms));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * The day the occurrence ITSELF lands on: the day of its instant on the caller's clock where it
 * has one, the civil date it names where it does not. `null` for a response carrying no moment.
 */
export function occurrenceOwnDay(source: OccurrencePlacement, zone: string | null): IsoDay | null {
  const at = occurrenceInstant(source);
  if (at !== null) return dayOn(at, zone);
  return calendarDayOf(source.moment?.date ?? null);
}

/**
 * R17, whole: the day this occurrence is drawn on and the instant it stands at, or `null` when
 * nothing placeable came back. The ONE authority — every calendar asks this.
 *
 * Undiverged (`displayDate` byte-equal to `eventDate`): the occurrence's own day and hour.
 * Diverged: `displayDate` is the recorded finish or start. Where it lands on the slot's own day the
 * slot keeps its hour; where it lands on another day, that day and that instant win.
 */
export function occurrencePlacement(
  source: OccurrencePlacement,
  zone: string | null,
): PlacedOccurrence | null {
  const stamp = calendarDayOf(source.displayDate);
  if (stamp === null) return null;

  const slotMs = occurrenceInstant(source);
  const own = occurrenceOwnDay(source, zone);
  const undiverged =
    source.eventDate !== null &&
    source.eventDate !== undefined &&
    source.displayDate === source.eventDate;
  if (undiverged) return { day: own ?? stamp, atMs: slotMs };

  const displayMs = /T\d/.test(source.displayDate ?? '') ? parseInstant(source.displayDate) : null;
  if (displayMs === null) return { day: stamp, atMs: own === stamp ? slotMs : null };
  const displayDay = dayOn(displayMs, zone);
  return displayDay === own ? { day: own, atMs: slotMs } : { day: displayDay, atMs: displayMs };
}

/** The square this occurrence lands in, or `null` when nothing placeable came back. */
export function occurrencePlacementDay(
  source: OccurrencePlacement,
  zone: string | null,
): IsoDay | null {
  return occurrencePlacement(source, zone)?.day ?? null;
}
